/**
 * Administration (nur tester:reviewer, PRD §8).
 * Muster netzfabriks-erp: KI-Anbieter-Keys (verschlüsselt in DB) +
 * Per-Service-Konfiguration + Verbrauchsübersicht + Service-API-Token
 * für die ERP-Anbindung.
 */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"
import type { AdminProvider, AdminService } from "@/types"

type Tab = "anbieter" | "dienste" | "verbrauch" | "service-api"

export function AdminKiPage() {
  const istReviewer = useAuthStore((s) => s.isReviewer())
  // Eingeschränkte Reviewer sehen in der Administration nur den Verbrauch.
  const eingeschraenkt = useAuthStore((s) => s.isEingeschraenkt())
  const [tab, setTab] = useState<Tab>(eingeschraenkt ? "verbrauch" : "anbieter")

  if (!istReviewer) {
    return (
      <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
        Keine Berechtigung — die KI-Administration ist netz-fabrik vorbehalten.
      </p>
    )
  }

  return (
    <div className="mx-auto max-w-[880px]">
      <h1 className="mb-5 text-[26px] font-bold tracking-tight">Administration</h1>
      <div className="mb-5 inline-flex rounded-[10px] bg-secondary p-[3px]">
        {(
          [
            ["anbieter", "Anbieter"],
            ["dienste", "Dienste"],
            ["verbrauch", "Verbrauch"],
            ["service-api", "Service-API"],
          ] as const
        )
          .filter(([wert]) => !eingeschraenkt || wert === "verbrauch")
          .map(([wert, label]) => (
            <button
              key={wert}
              type="button"
              onClick={() => setTab(wert)}
              className={`rounded-lg px-[18px] py-2 text-[13px] font-semibold transition-colors ${
                tab === wert
                  ? "bg-card text-foreground shadow-card"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
      </div>
      {!eingeschraenkt && tab === "anbieter" && <AnbieterTab />}
      {!eingeschraenkt && tab === "dienste" && <DiensteTab />}
      {tab === "verbrauch" && <VerbrauchTab />}
      {!eingeschraenkt && tab === "service-api" && <ServiceApiTab />}
    </div>
  )
}

// =============================================================================
// ANBIETER
// =============================================================================

function AnbieterKarte({ provider }: { provider: AdminProvider }) {
  const queryClient = useQueryClient()
  const [apiKey, setApiKey] = useState("")
  const [model, setModel] = useState(provider.model)
  const [endpoint, setEndpoint] = useState(provider.endpoint)
  const [modelle, setModelle] = useState<string[] | null>(null)

  const speichern = useMutation({
    mutationFn: () =>
      api.admin.providers.update(provider.provider, {
        ...(apiKey !== "" ? { apiKey } : {}),
        model,
        ...(provider.hatEndpoint ? { endpoint } : {}),
      }),
    onSuccess: () => {
      setApiKey("")
      void queryClient.invalidateQueries({ queryKey: ["admin", "providers"] })
      toast.success(`${provider.name} gespeichert`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const modelleLaden = useMutation({
    mutationFn: () => api.admin.providers.models(provider.provider),
    onSuccess: (liste) => setModelle(liste.map((m) => m.id)),
    onError: (e) => toast.error(apiError(e)),
  })

  // Verbindungs-Test: feste Frage an den Anbieter, Antwort wird angezeigt
  const testen = useMutation({
    mutationFn: () => api.admin.providers.test(provider.provider),
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <div className="rounded-xl border bg-card p-5 shadow-card">
      <div className="mb-3.5 flex items-center justify-between">
        <h2 className="text-[15px] font-bold">{provider.name}</h2>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-bold ${
            provider.isConfigured
              ? "bg-success/10 text-success"
              : "bg-secondary text-muted-foreground"
          }`}
        >
          {provider.isConfigured ? "Konfiguriert" : "Nicht konfiguriert"}
        </span>
      </div>
      <div className="flex flex-col gap-3 text-[13px]">
        <label>
          <span className="mb-1.5 block font-semibold text-foreground/70">API-Key</span>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={provider.apiKeyMasked || "Noch kein Key gespeichert"}
            className="w-full rounded-lg border px-3 py-2"
          />
        </label>
        {provider.hatEndpoint && (
          <label>
            <span className="mb-1.5 block font-semibold text-foreground/70">Endpoint</span>
            <input
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
            />
          </label>
        )}
        <label>
          <span className="mb-1.5 block font-semibold text-foreground/70">Modell</span>
          <div className="flex gap-2">
            {modelle ? (
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full rounded-lg border px-3 py-2"
              >
                {!modelle.includes(model) && model && <option value={model}>{model}</option>}
                {modelle.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder={provider.defaultModel}
                className="w-full rounded-lg border px-3 py-2"
              />
            )}
            <button
              type="button"
              onClick={() => modelleLaden.mutate()}
              disabled={!provider.isConfigured || modelleLaden.isPending}
              title="Modell-Liste live vom Anbieter abrufen"
              className="shrink-0 whitespace-nowrap rounded-lg bg-secondary px-3 py-2 font-semibold hover:bg-secondary/80 disabled:opacity-40"
            >
              {modelleLaden.isPending ? "Lade …" : "Modelle laden"}
            </button>
          </div>
        </label>
        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => testen.mutate()}
            disabled={!provider.isConfigured || testen.isPending}
            title="Testfrage an den Anbieter senden (prüft Key/Endpoint/Modell)"
            className="rounded-lg bg-secondary px-4 py-2 font-semibold hover:bg-secondary/80 disabled:opacity-40"
          >
            {testen.isPending ? "Teste …" : "Test"}
          </button>
          <button
            type="button"
            onClick={() => speichern.mutate()}
            disabled={speichern.isPending}
            className="rounded-lg bg-primary px-[18px] py-2 font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            Speichern
          </button>
        </div>

        {testen.isError && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {apiError(testen.error)}
          </p>
        )}
        {testen.data && (
          <div className="rounded-[10px] border border-primary/15 bg-primary/5 px-3.5 py-3 text-xs">
            <p className="mb-1 font-bold text-primary">
              Test erfolgreich — {testen.data.model} · {(testen.data.dauerMs / 1000).toFixed(1)} s
            </p>
            <p className="whitespace-pre-wrap">{testen.data.antwort}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function AnbieterTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "providers"],
    queryFn: api.admin.providers.list,
  })
  if (isLoading) return <p className="text-muted-foreground">Lade Anbieter …</p>
  if (error != null || !data)
    return <p className="text-destructive">{apiError(error)}</p>
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        API-Keys werden AES-256-verschlüsselt in der Datenbank gespeichert — nie in
        Umgebungsvariablen. Der Kunde wählt den Anbieter, konfiguriert wird hier durch
        netz-fabrik.
      </p>
      {data.map((p) => (
        <AnbieterKarte key={p.provider} provider={p} />
      ))}
    </div>
  )
}

// =============================================================================
// DIENSTE
// =============================================================================

function DienstKarte({
  dienst,
  providerNamen,
}: {
  dienst: AdminService
  providerNamen: string[]
}) {
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState(dienst.provider)
  const [model, setModel] = useState(dienst.model ?? "")
  const [fallbackProvider, setFallbackProvider] = useState(dienst.fallbackProvider ?? "default")
  const [temperature, setTemperature] = useState(String(dienst.temperature))
  const [prompt, setPrompt] = useState(dienst.systemPrompt)

  const invalidieren = () =>
    void queryClient.invalidateQueries({ queryKey: ["admin", "services"] })

  const speichern = useMutation({
    mutationFn: () =>
      api.admin.services.update(dienst.serviceId, {
        provider,
        model: model || undefined,
        fallbackProvider,
        temperature: Number(temperature),
        systemPrompt: prompt,
      }),
    onSuccess: () => {
      invalidieren()
      toast.success(`${dienst.name} gespeichert`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const promptReset = useMutation({
    mutationFn: () => api.admin.services.resetPrompt(dienst.serviceId),
    onSuccess: (neu) => {
      setPrompt(neu.systemPrompt)
      invalidieren()
      toast.success("Prompt auf Standard zurückgesetzt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const auswahl = ["default", ...providerNamen]

  return (
    <div className="rounded-xl border bg-card p-5 text-[13px] shadow-card">
      <h2 className="text-[15px] font-bold">{dienst.name}</h2>
      <p className="mb-3.5 text-muted-foreground">{dienst.description}</p>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <label>
          <span className="mb-1.5 block font-semibold text-foreground/70">Anbieter</span>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full rounded-lg border px-2 py-2"
          >
            {auswahl.map((p) => (
              <option key={p} value={p}>
                {p === "default" ? "Standard" : p}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="mb-1.5 block font-semibold text-foreground/70">Modell</span>
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Anbieter-Standard"
            className="w-full rounded-lg border px-2 py-2"
          />
        </label>
        <label>
          <span className="mb-1.5 block font-semibold text-foreground/70">Fallback</span>
          <select
            value={fallbackProvider}
            onChange={(e) => setFallbackProvider(e.target.value)}
            className="w-full rounded-lg border px-2 py-2"
          >
            {auswahl.map((p) => (
              <option key={p} value={p}>
                {p === "default" ? "Standard" : p}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="mb-1.5 block font-semibold text-foreground/70">Temperatur</span>
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            className="w-full rounded-lg border px-2 py-2"
          />
        </label>
      </div>
      <label className="mt-3 block">
        <span className="mb-1.5 block font-semibold text-foreground/70">Systemprompt</span>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={5}
          className="w-full rounded-lg border px-3 py-2 font-mono text-xs text-foreground/80"
        />
      </label>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => promptReset.mutate()}
          className="rounded-lg bg-secondary px-4 py-2 font-semibold hover:bg-secondary/80"
        >
          Prompt zurücksetzen
        </button>
        <button
          type="button"
          onClick={() => speichern.mutate()}
          disabled={speichern.isPending}
          className="rounded-lg bg-primary px-[18px] py-2 font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          Speichern
        </button>
      </div>
    </div>
  )
}

function DiensteTab() {
  const dienste = useQuery({ queryKey: ["admin", "services"], queryFn: api.admin.services.list })
  const provider = useQuery({
    queryKey: ["admin", "providers"],
    queryFn: api.admin.providers.list,
  })
  if (dienste.isLoading) return <p className="text-muted-foreground">Lade Dienste …</p>
  if (dienste.error != null || !dienste.data)
    return <p className="text-destructive">{apiError(dienste.error)}</p>
  const providerNamen = (provider.data ?? []).map((p) => p.provider)
  return (
    <div className="flex flex-col gap-3">
      {dienste.data.map((d) => (
        <DienstKarte key={d.serviceId} dienst={d} providerNamen={providerNamen} />
      ))}
    </div>
  )
}

// =============================================================================
// VERBRAUCH
// =============================================================================

function VerbrauchTab() {
  const [monat, setMonat] = useState<string>("")
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "usage", monat],
    queryFn: () => api.admin.usage(monat || undefined),
  })
  return (
    <div>
      <label className="mb-3 block text-sm">
        <span className="mb-1 block font-medium">Monat (leer = gesamt)</span>
        <input
          type="month"
          value={monat}
          onChange={(e) => setMonat(e.target.value)}
          className="rounded-md border px-3 py-1.5"
        />
      </label>
      {isLoading && <p className="text-muted-foreground">Lade Verbrauch …</p>}
      {data && data.length === 0 && (
        <p className="rounded-lg border bg-card px-4 py-6 text-center text-muted-foreground">
          Noch kein Verbrauch erfasst.
        </p>
      )}
      {data && data.length > 0 && (
        <div className="overflow-hidden rounded-xl border bg-card shadow-card">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b text-left text-xs font-bold uppercase tracking-wide text-muted-foreground/70">
                <th className="py-3 pl-5 pr-4 font-bold">Dienst</th>
                <th className="py-3 pr-4 font-bold">Anbieter</th>
                <th className="py-3 pr-4 font-bold">Modell</th>
                <th className="py-3 pr-4 text-right font-bold">Aufrufe</th>
                <th className="py-3 pr-4 text-right font-bold">Input-Tokens</th>
                <th className="py-3 pr-5 text-right font-bold">Output-Tokens</th>
              </tr>
            </thead>
            <tbody>
              {data.map((z, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="py-3 pl-5 pr-4">{z.serviceId}</td>
                  <td className="py-3 pr-4">{z.provider}</td>
                  <td className="py-3 pr-4">{z.model}</td>
                  <td className="py-3 pr-4 text-right">{z.calls}</td>
                  <td className="py-3 pr-4 text-right">{z.inputTokens.toLocaleString("de-DE")}</td>
                  <td className="py-3 pr-5 text-right">{z.outputTokens.toLocaleString("de-DE")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// SERVICE-API (ERP-Anbindung)
// =============================================================================

function ServiceApiTab() {
  const [sichtbar, setSichtbar] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "service-token"],
    queryFn: () => api.admin.serviceToken(),
  })

  // Webadresse der Instanz — das ERP ergänzt /tester/api/v1 selbst.
  const webadresse = window.location.origin

  const kopieren = async (wert: string, was: string) => {
    try {
      await navigator.clipboard.writeText(wert)
      toast.success(`${was} kopiert`)
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren")
    }
  }

  if (isLoading) return <p className="text-muted-foreground">Lade Service-API-Status …</p>

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-5 shadow-card">
        <h2 className="mb-1 text-[15px] font-bold">Anbindung ans netzfabriks-erp</h2>
        <p className="mb-4 text-[13px] text-muted-foreground">
          Das ERP fragt diese Instanz über eine read-only Service-API ab
          (Meldungs-Stände, Statistiken, Suche — nie Entwürfe). Im ERP unter
          Einstellungen → Integrationen → ninox-tester die Webadresse und
          den Service-Token eintragen.
        </p>

        <div className="mb-4">
          <span className="mb-1 block text-sm font-medium">Webadresse</span>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border bg-secondary/50 px-3 py-1.5 text-[13px]">
              {webadresse}
            </code>
            <button
              type="button"
              onClick={() => void kopieren(webadresse, "Webadresse")}
              className="rounded-md border px-3 py-1.5 text-[13px] font-semibold hover:bg-secondary"
            >
              Kopieren
            </button>
          </div>
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium">Service-Token</span>
          {data?.konfiguriert && data.token ? (
            <>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded-md border bg-secondary/50 px-3 py-1.5 text-[13px]">
                  {sichtbar ? data.token : "•".repeat(24)}
                </code>
                <button
                  type="button"
                  onClick={() => setSichtbar((v) => !v)}
                  className="rounded-md border px-3 py-1.5 text-[13px] font-semibold hover:bg-secondary"
                >
                  {sichtbar ? "Verbergen" : "Anzeigen"}
                </button>
                <button
                  type="button"
                  onClick={() => void kopieren(data.token ?? "", "Service-Token")}
                  className="rounded-md border px-3 py-1.5 text-[13px] font-semibold hover:bg-secondary"
                >
                  Kopieren
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Der Token ist in der Server-Umgebung gesetzt (SERVICE_TOKEN) und
                gilt für diese Instanz. Vertraulich behandeln — er gewährt
                Lesezugriff auf alle Meldungs-Stände (ohne Entwürfe).
              </p>
            </>
          ) : (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
              Kein Service-Token konfiguriert — die Service-API ist deaktiviert.
              Zum Aktivieren SERVICE_TOKEN in der Server-.env setzen (Tresor:
              SERVICE_TOKEN_&lt;STAGE&gt;) und die Instanz neu starten.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
